import { FC } from 'react';

import { formatPrice } from '@shared/utils/formatPrice';

import { ReissueStatPerItem } from './types';

interface ReissueSectionProps {
  reissueStatsByItem: ReissueStatPerItem[];
  totalReissueRemaining: number;
  showAdditionalForm: boolean;
  setShowAdditionalForm: (v: boolean) => void;
  reissueGiftItemId: string;
  setReissueGiftItemId: (v: string) => void;
  additionalCount: number;
  setAdditionalCount: (v: number) => void;
  selectedReissueStat: ReissueStatPerItem | undefined;
  isCreatingAdditional: boolean;
  handleCreateAdditional: () => Promise<void>;
}

export const ReissueSection: FC<ReissueSectionProps> = ({
  reissueStatsByItem,
  totalReissueRemaining,
  showAdditionalForm,
  setShowAdditionalForm,
  reissueGiftItemId,
  setReissueGiftItemId,
  additionalCount,
  setAdditionalCount,
  selectedReissueStat,
  isCreatingAdditional,
  handleCreateAdditional,
}) => {
  return (
    <div className="shrink-0 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5">
      <div className="flex items-center justify-between">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <span className="text-sm font-medium text-amber-800">재발행 쿠폰</span>
          {reissueStatsByItem.map((s) => (
            <span
              key={s.giftItemId}
              className="flex items-center gap-1.5 text-xs text-amber-700"
            >
              <span className="font-medium">{s.name}</span>
              <span className="text-amber-500">
                {s.additionalIssued}/{s.sharedNotViewed}
              </span>
              {s.remaining > 0 ? (
                <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-700">
                  잔여 {s.remaining}
                </span>
              ) : (
                <span className="rounded-full bg-red-100 px-1.5 py-0.5 text-[10px] font-bold text-red-700">
                  한도 초과
                </span>
              )}
            </span>
          ))}
        </div>
        {!showAdditionalForm && totalReissueRemaining > 0 && (
          <button
            onClick={() => setShowAdditionalForm(true)}
            className="shrink-0 rounded-lg bg-amber-600 px-4 py-1.5 text-sm font-bold text-white hover:bg-amber-700"
          >
            쿠폰 재발행
          </button>
        )}
      </div>

      {showAdditionalForm && (
        <div className="mt-3 flex flex-wrap items-end gap-3 border-t border-amber-200 pt-3">
          <label className="flex flex-col gap-1 text-sm text-amber-800">
            <span className="font-medium">상품 선택</span>
            <select
              value={reissueGiftItemId}
              onChange={(e) => {
                setReissueGiftItemId(e.target.value);
                setAdditionalCount(1);
              }}
              className="rounded-lg border border-amber-300 bg-white px-2 py-2 text-sm focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
            >
              {reissueStatsByItem.map((s) => (
                <option
                  key={s.giftItemId}
                  value={s.giftItemId}
                  disabled={s.remaining <= 0}
                >
                  {s.name} · {formatPrice(s.priceRetail)}원 (잔여{' '}
                  {Math.max(0, s.remaining)})
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-2 text-sm text-amber-800">
            수량
            <input
              type="number"
              min={1}
              max={Math.max(1, selectedReissueStat?.remaining ?? 1)}
              value={additionalCount}
              onChange={(e) =>
                setAdditionalCount(
                  Math.max(
                    1,
                    Math.min(
                      Math.max(1, selectedReissueStat?.remaining ?? 1),
                      Number(e.target.value),
                    ),
                  ),
                )
              }
              className="w-20 rounded-lg border border-amber-300 px-2 py-1.5 text-sm focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
            />
            <span className="text-xs text-amber-500">
              (최대 {Math.max(0, selectedReissueStat?.remaining ?? 0)}개)
            </span>
          </label>
          <button
            onClick={handleCreateAdditional}
            disabled={
              isCreatingAdditional ||
              additionalCount < 1 ||
              !selectedReissueStat ||
              selectedReissueStat.remaining < 1
            }
            className="rounded-lg bg-amber-600 px-4 py-1.5 text-sm font-bold text-white hover:bg-amber-700 disabled:opacity-50"
          >
            {isCreatingAdditional ? '처리중...' : '발행'}
          </button>
          <button
            onClick={() => {
              setShowAdditionalForm(false);
              setAdditionalCount(1);
            }}
            className="rounded-lg border border-amber-300 px-3 py-1.5 text-sm font-medium text-amber-700 hover:bg-amber-100"
          >
            취소
          </button>
        </div>
      )}
    </div>
  );
};
